import { Button, Col, Container, Form, Row, Table } from "react-bootstrap";
import { Dispatch, SetStateAction, useState } from "react";
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook";
import { useBulkEditPropertiesMutation, useGetPropertyAmenitiesQuery } from "../../../services/bClientApi";
import { PropertyType } from "../../../Types/propertyTypes";
import usePropertyAmenityCopy, {
    AmenityCopyMode,
    formatAmenityCopyPlan,
    formatAmenityCopyResult,
} from "../../../Hooks/UseAmenities/usePropertyAmenityCopyHook";
import CopyAmenitiesOptions from "../../../Components/PropertyAmenity/CopyAmenitiesOptions/CopyAmenitiesOptions";

// ─── Copyable Fields Config ───────────────────────────────────────────────────
// To add a new field: add an entry with:
//   get(p)              – extract the value from the source property
//   apply(update, value, target) – merge the value into the update object,
//                                  spreading sibling data when the field is nested
//   About apply arguments:
//       update is the accumulating update object for the target property
//       value is the extracted value from the source
//       target is the full target property (useful for merging nested fields)
// ─────────────────────────────────────────────────────────────────────────────
interface ApplyOptions {
    // Empty means every linkedService entry is copied
    linkedServiceNames: string[];
    // Empty means every about section is copied
    aboutSectionIds: string[];
}

interface CopyableField {
    id: string;
    label: string;
    path: string;
    get: (p: PropertyType) => any;
    apply: (update: any, value: any, target: PropertyType, options: ApplyOptions) => any;
    inputType: 'text' | 'textarea' | 'number' | 'boolean' | 'coordinates' | 'json';
    parse: (raw: string) => any;
    stringify: (value: any) => string;
}

// Value used to select "Manual Entry" instead of a source property
const MANUAL_SOURCE_ID = '__manual__';
const LINKED_SERVICES_FIELD_ID = 'linkedServices';
const ABOUT_SECTIONS_FIELD_ID = 'about.sections';

// Replaces target entries sharing a key with the copied ones and appends the rest
const mergeByKey = (existing: any[], incoming: any[], key: string, selected: string[]) => {
    const toCopy = selected.length ? incoming.filter((item) => selected.includes(item[key])) : incoming;
    const copiedKeys = new Set(toCopy.map((item) => item[key]));
    return [...existing.filter((item) => !copiedKeys.has(item[key])), ...toCopy];
};

// ─── Manual Entry Parsers ──────────────────────────────────────────────────
const parseManualText = (raw: string) => raw;

const parseManualNumber = (raw: string) => {
    const num = Number(raw);
    if (raw.trim() === '' || Number.isNaN(num)) throw new Error('Enter a valid number');
    return num;
};

const parseManualBoolean = (raw: string) => raw === 'true';

const parseManualCoordinates = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim());
    const nums = parts.map(Number);
    if (parts.length !== 2 || nums.some((n) => Number.isNaN(n))) {
        throw new Error('Enter coordinates as "longitude, latitude"');
    }
    return nums;
};

const parseManualJson = (raw: string) => {
    if (!raw.trim()) throw new Error('Enter valid JSON');
    return JSON.parse(raw);
};

const parseManualLinkedServices = (raw: string) => {
    const parsed = parseManualJson(raw);
    if (!Array.isArray(parsed) || parsed.some((s) => !s || typeof s.linkedService !== 'string')) {
        throw new Error('Enter an array of objects with a "linkedService" value');
    }
    return parsed;
};

const parseManualAboutSections = (raw: string) => {
    const parsed = parseManualJson(raw);
    if (!Array.isArray(parsed) || parsed.some((s) => !s || typeof s.sectionId !== 'string')) {
        throw new Error('Enter an array of objects with a "sectionId" value');
    }
    return parsed;
};

// ─── Manual Entry Stringifiers (inverse of the parsers, for the "Fill" button) ─
const stringifyManualText = (value: any) => value ?? '';

const stringifyManualNumber = (value: any) => (value === undefined || value === null ? '' : String(value));

const stringifyManualBoolean = (value: any) => (value ? 'true' : 'false');

const stringifyManualCoordinates = (value: any) => (Array.isArray(value) ? value.join(', ') : '');

const stringifyManualJson = (value: any) => (value === undefined || value === null ? '' : JSON.stringify(value, null, 2));

const COPYABLE_FIELDS: CopyableField[] = [
    {
        id: 'coordinates',
        label: 'Coordinates',
        path: 'coordinates',
        get: (p) => p.coordinates,
        apply: (update, value) => ({ ...update, coordinates: value }),
        inputType: 'coordinates',
        parse: parseManualCoordinates,
        stringify: stringifyManualCoordinates,
    },
    {
        id: 'hideExactCoordinates',
        label: 'Hide Exact Coordinates',
        path: 'hideExactCoordinates',
        get: (p) => p.hideExactCoordinates,
        apply: (update, value) => ({ ...update, hideExactCoordinates: value }),
        inputType: 'boolean',
        parse: parseManualBoolean,
        stringify: stringifyManualBoolean,
    },
    {
        id: 'description',
        label: 'Description',
        path: 'description',
        get: (p) => p.description,
        apply: (update, value) => ({ ...update, description: value }),
        inputType: 'textarea',
        parse: parseManualText,
        stringify: stringifyManualText,
    },
    {
        id: 'type',
        label: 'Type',
        path: 'type',
        get: (p) => p.type,
        apply: (update, value) => ({ ...update, type: value }),
        inputType: 'text',
        parse: parseManualText,
        stringify: stringifyManualText,
    },
    {
        id: 'registrationInfo',
        label: 'Registration Info',
        path: 'registrationInfo',
        get: (p) => p.registrationInfo,
        apply: (update, value) => ({ ...update, registrationInfo: value }),
        inputType: 'json',
        parse: parseManualJson,
        stringify: stringifyManualJson,
    },
    {
        id: 'spaces',
        label: 'Spaces',
        path: 'spaces',
        get: (p) => p.spaces,
        apply: (update, value) => ({ ...update, spaces: value }),
        inputType: 'json',
        parse: parseManualJson,
        stringify: stringifyManualJson,
    },
    {
        id: 'platformSettings',
        label: 'Platform Settings',
        path: 'platformSettings',
        get: (p) => p.platformSettings,
        apply: (update, value, target) => ({
            ...update,
            platformSettings: { ...target.platformSettings, ...(update.platformSettings || {}), ...value },
        }),
        inputType: 'json',
        parse: parseManualJson,
        stringify: stringifyManualJson,
    },
    {
        id: 'about.summary',
        label: 'About - Summary',
        path: 'about.summary',
        get: (p) => p.about?.summary,
        apply: (update, value, target) => ({
            ...update,
            about: { ...target.about, ...(update.about || {}), summary: value },
        }),
        inputType: 'textarea',
        parse: parseManualText,
        stringify: stringifyManualText,
    },
    {
        id: ABOUT_SECTIONS_FIELD_ID,
        label: 'About - Sections',
        path: 'about.sections',
        get: (p) => p.about?.sections ?? [],
        apply: (update, value, target, options) => ({
            ...update,
            about: {
                ...target.about,
                ...(update.about || {}),
                sections: mergeByKey(
                    update.about?.sections || target.about?.sections || [],
                    value || [],
                    'sectionId',
                    options.aboutSectionIds
                ),
            },
        }),
        inputType: 'json',
        parse: parseManualAboutSections,
        stringify: stringifyManualJson,
    },
    {
        id: 'pricing.extraFees',
        label: 'Pricing - Extra Fees',
        path: 'pricing.extraFees',
        get: (p) => p.pricing?.extraFees,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), extraFees: value },
        }),
        inputType: 'json',
        parse: parseManualJson,
        stringify: stringifyManualJson,
    },
    {
        id: 'pricing.baseRate',
        label: 'Pricing - Base Rate',
        path: 'pricing.baseRate',
        get: (p) => p.pricing?.baseRate,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), baseRate: value },
        }),
        inputType: 'number',
        parse: parseManualNumber,
        stringify: stringifyManualNumber,
    },
    {
        id: 'pricing.deposit',
        label: 'Pricing - Deposit',
        path: 'pricing.deposit',
        get: (p) => p.pricing?.deposit,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), deposit: value },
        }),
        inputType: 'number',
        parse: parseManualNumber,
        stringify: stringifyManualNumber,
    },
    {
        id: 'pricing.cleaning',
        label: 'Pricing - Cleaning',
        path: 'pricing.cleaning',
        get: (p) => p.pricing?.cleaning,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), cleaning: value },
        }),
        inputType: 'number',
        parse: parseManualNumber,
        stringify: stringifyManualNumber,
    },
    {
        id: 'pricing.basicCleaning',
        label: 'Pricing - Basic Cleaning',
        path: 'pricing.basicCleaning',
        get: (p) => p.pricing?.basicCleaning,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), basicCleaning: value },
        }),
        inputType: 'number',
        parse: parseManualNumber,
        stringify: stringifyManualNumber,
    },
    // tax pricing.tax
    {
        id: 'pricing.tax',
        label: 'Pricing - Tax',
        path: 'pricing.tax',
        get: (p) => p.pricing?.tax,
        apply: (update, value, target) => ({
            ...update,
            pricing: { ...target.pricing, ...(update.pricing || {}), tax: value },
        }),
        inputType: 'number',
        parse: parseManualNumber,
        stringify: stringifyManualNumber,
    },
    {
        id: LINKED_SERVICES_FIELD_ID,
        label: 'Linked Services',
        path: 'linkedServices',
        get: (p) => p.linkedServices ?? [],
        apply: (update, value, target, options) => ({
            ...update,
            linkedServices: mergeByKey(
                update.linkedServices || target.linkedServices || [],
                value || [],
                'linkedService',
                options.linkedServiceNames
            ),
        }),
        inputType: 'json',
        parse: parseManualLinkedServices,
        stringify: stringifyManualJson,
    }
];

export default function BulkPropertyPage() {

    const { myProperties } = useMyProperties({ allData: true });

    const [publicPropertiesOnly, setPublicPropertiesOnly] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [sourceId, setSourceId] = useState('');
    const [targetIds, setTargetIds] = useState<string[]>([]);
    const [selectedFields, setSelectedFields] = useState<string[]>(
        // COPYABLE_FIELDS.map((f) => f.id)
        []
    );
    const [manualValues, setManualValues] = useState<Record<string, string>>({});
    const [selectedLinkedServices, setSelectedLinkedServices] = useState<string[]>([]);
    const [selectedAboutSections, setSelectedAboutSections] = useState<string[]>([]);
    const [resultMessage, setResultMessage] = useState('');
    const [copyAmenities, setCopyAmenities] = useState(false);
    const [amenityMode, setAmenityMode] = useState<AmenityCopyMode>('add-missing');
    const [selectedAmenityIds, setSelectedAmenityIds] = useState<string[]>([]);
    const [isApplying, setIsApplying] = useState(false);

    const [bulkEditProperties] = useBulkEditPropertiesMutation();
    const { planCopy, executePlan } = usePropertyAmenityCopy();

    const propertyList = Object.values(myProperties) as PropertyType[];

    const filteredList = propertyList.filter((p) => {
        if (publicPropertiesOnly && !p.public) return false; // Exclude non-public properties

        if (filterText) {
           return p.title?.toLowerCase().includes(filterText.toLowerCase()) ||
            p._id?.toLowerCase().includes(filterText.toLowerCase());
        }
        return true;
    })

    const isManualSource = sourceId === MANUAL_SOURCE_ID;
    const sourceProperty = sourceId && !isManualSource ? (myProperties[sourceId] as PropertyType) : null;

    const { data: sourceAmenityData } = useGetPropertyAmenitiesQuery({
        propertyAmenitiesQuery: { propertyId: sourceId },
        pagination: { page: 1, limit: 500, all: true },
    }, { skip: !sourceProperty });
    const sourceAmenities = sourceProperty ? (sourceAmenityData?.docs || []) : [];
    const amenitiesEnabled = copyAmenities && !!sourceProperty && sourceAmenities.length > 0;
    const getAvailableKeys = (fieldId: string, key: string): string[] => {
        const field = COPYABLE_FIELDS.find((f) => f.id === fieldId)!;
        let entries: any[] = [];
        try {
            entries = isManualSource
                ? field.parse(manualValues[fieldId] ?? '')
                : field.get(sourceProperty!) ?? [];
        } catch {
            // Manual JSON is invalid or empty; no keys to offer yet
        }
        return Array.from(new Set(entries.map((e) => e[key] as string)));
    };

    const toggleSelection = (setter: Dispatch<SetStateAction<string[]>>, name: string) => {
        setter((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
    };

    const renderKeySelector = (
        idPrefix: string,
        itemLabel: string,
        names: string[],
        selected: string[],
        setter: Dispatch<SetStateAction<string[]>>
    ) => {
        if (!isManualSource && !sourceProperty) return null;
        return (
            <div className="ms-4 mb-2">
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Only copy these {itemLabel} (none checked copies all). Matching entries on targets are replaced, others are added.
                </div>
                {names.length === 0 && (
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>No {itemLabel} found on the source.</div>
                )}
                {names.map((name) => (
                    <Form.Check
                        key={name}
                        inline
                        type="checkbox"
                        id={`${idPrefix}-${name}`}
                        label={name}
                        checked={selected.includes(name)}
                        onChange={() => toggleSelection(setter, name)}
                    />
                ))}
            </div>
        );
    };

    const renderFieldSelector = (fieldId: string) => {
        if (fieldId === LINKED_SERVICES_FIELD_ID) {
            return renderKeySelector(
                'linked-service', 'linked services',
                getAvailableKeys(LINKED_SERVICES_FIELD_ID, 'linkedService'),
                selectedLinkedServices, setSelectedLinkedServices
            );
        }
        if (fieldId === ABOUT_SECTIONS_FIELD_ID) {
            return renderKeySelector(
                'about-section', 'about sections',
                getAvailableKeys(ABOUT_SECTIONS_FIELD_ID, 'sectionId'),
                selectedAboutSections, setSelectedAboutSections
            );
        }
        return null;
    };

    const setManualValue = (fieldId: string, value: string) => {
        setManualValues((prev) => ({ ...prev, [fieldId]: value }));
    };

    const getManualParseError = (field: CopyableField): string | null => {
        try {
            field.parse(manualValues[field.id] ?? '');
            return null;
        } catch (err) {
            return err instanceof Error ? err.message : 'Invalid value';
        }
    };

    const fillManualValuesFromFirstTarget = () => {
        const firstTarget = myProperties[targetIds[0]] as PropertyType;
        if (!firstTarget) return;
        setManualValues((prev) => {
            const next = { ...prev };
            for (const field of COPYABLE_FIELDS) {
                if (!selectedFields.includes(field.id)) continue;
                next[field.id] = field.stringify(field.get(firstTarget));
            }
            return next;
        });
    };

    const renderManualInput = (field: CopyableField) => {
        const raw = manualValues[field.id] ?? '';
        const error = getManualParseError(field);

        let control;
        switch (field.inputType) {
            case 'boolean':
                control = (
                    <Form.Check
                        type="switch"
                        id={`manual-${field.id}`}
                        label={raw === 'true' ? 'True' : 'False'}
                        checked={raw === 'true'}
                        onChange={(e) => setManualValue(field.id, e.target.checked ? 'true' : 'false')}
                    />
                );
                break;
            case 'number':
                control = (
                    <Form.Control
                        type="number"
                        size="sm"
                        value={raw}
                        placeholder="Enter a number"
                        onChange={(e) => setManualValue(field.id, e.target.value)}
                    />
                );
                break;
            case 'coordinates':
                control = (
                    <Form.Control
                        type="text"
                        size="sm"
                        value={raw}
                        placeholder="Longitude, Latitude"
                        onChange={(e) => setManualValue(field.id, e.target.value)}
                    />
                );
                break;
            case 'json':
                control = (
                    <Form.Control
                        as="textarea"
                        rows={3}
                        size="sm"
                        style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        value={raw}
                        placeholder="Enter valid JSON"
                        onChange={(e) => setManualValue(field.id, e.target.value)}
                    />
                );
                break;
            default:
                control = (
                    <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={raw}
                        placeholder="Enter a value"
                        onChange={(e) => setManualValue(field.id, e.target.value)}
                    />
                );
        }

        return (
            <div className="ms-4 mb-2" style={{ maxWidth: 420 }}>
                {control}
                {error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{error}</div>}
            </div>
        );
    };

    const toggleField = (fieldId: string) => {
        setSelectedFields((prev) =>
            prev.includes(fieldId) ? prev.filter((f) => f !== fieldId) : [...prev, fieldId]
        );
    };

    const toggleTarget = (propertyId: string) => {
        setTargetIds((prev) =>
            prev.includes(propertyId)
                ? prev.filter((id) => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const selectAllTargets = () => {
        setTargetIds(
            filteredList
                .map((p) => p._id)
                .filter((id) => id !== sourceId)
        );
    };

    const buildPayload = () => {
        if (!targetIds.length || !selectedFields.length) return [];
        if (!isManualSource && !sourceProperty) return [];
        const fieldsToApply = COPYABLE_FIELDS.filter((f) => selectedFields.includes(f.id));
        return targetIds.map((targetId) => {
            const target = myProperties[targetId] as PropertyType;
            let update: any = { _id: targetId };
            for (const field of fieldsToApply) {
                const value = isManualSource
                    ? field.parse(manualValues[field.id] ?? '')
                    : field.get(sourceProperty!);
                update = field.apply(update, value, target, { linkedServiceNames: selectedLinkedServices, aboutSectionIds: selectedAboutSections });
            }
            return update;
        });
    };

    const handleApply = async (preview: boolean = false) => {
        const payload = buildPayload();
        if (!payload.length && !amenitiesEnabled) return;
        const messages: string[] = [];

        if (payload.length) {
            console.log('Copy payload:', payload);
            if (preview) {
                messages.push(`Previewing property field changes for ${payload.length} propert${payload.length === 1 ? 'y' : 'ies'}.`);
            }
        }

        if (amenitiesEnabled) {
            if (!preview && amenityMode === 'replace-all' && !window.confirm(
                `This deletes ALL amenities currently assigned to ${targetIds.length} propert${targetIds.length === 1 ? 'y' : 'ies'} and replaces them with the ones from ${sourceProperty!.title}. Continue?`
            )) {
                return;
            }
        }

        setIsApplying(true);
        try {
            if (payload.length && !preview) {
                await bulkEditProperties(payload).unwrap();
                messages.push(`Successfully updated ${payload.length} propert${payload.length === 1 ? 'y' : 'ies'}.`);
            }

            if (amenitiesEnabled) {
                const amenityPlans = [];
                for (const targetId of targetIds) {
                    amenityPlans.push(await planCopy(sourceAmenities, targetId, amenityMode, selectedAmenityIds));
                }
                if (preview) {
                    console.log('Amenity copy plans:', amenityPlans);
                    messages.push('Amenity changes (details in console):');
                    amenityPlans.forEach((plan) => {
                        messages.push(`  ${(myProperties[plan.propertyId] as PropertyType)?.title}: ${formatAmenityCopyPlan(plan)}`);
                    });
                } else {
                    messages.push('Amenity changes:');
                    for (const plan of amenityPlans) {
                        const result = await executePlan(plan);
                        messages.push(`  ${(myProperties[plan.propertyId] as PropertyType)?.title}: ${formatAmenityCopyResult(result)}`);
                    }
                }
            }
        } catch (err) {
            console.error('Bulk apply failed:', err);
            messages.push('Something went wrong. Check the console for details.');
        } finally {
            setIsApplying(false);
        }
        setResultMessage(messages.join('\n'));
    };

    const hasManualErrors = isManualSource && COPYABLE_FIELDS.some(
        (f) => selectedFields.includes(f.id) && getManualParseError(f)
    );

    const hasSomethingToCopy = selectedFields.length > 0 || amenitiesEnabled;

    const canApply = (isManualSource || !!sourceProperty) && targetIds.length > 0 && hasSomethingToCopy && !hasManualErrors && !isApplying;

    return (
        <Container>
            <Row className="mt-3 mb-2">
                <Col>
                    <h4>Copy Property Data</h4>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                        Select a source property, choose which fields to copy, then pick the target properties.
                    </p>
                </Col>
            </Row>

            {/* Fields to copy */}
            <Row className="mb-3">
                <Col>
                    <h6>Fields to Copy</h6>
                    {isManualSource && (
                        <div className="mb-2 d-flex align-items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={fillManualValuesFromFirstTarget}
                                disabled={!targetIds.length || !selectedFields.length}
                            >
                                Fill from First Selected Property
                            </Button>
                            {targetIds.length > 0 && (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    Uses values from {(myProperties[targetIds[0]] as PropertyType)?.title}
                                </span>
                            )}
                        </div>
                    )}
                    {COPYABLE_FIELDS.map((f) => (
                        <div key={f.id}>
                            <Form.Check
                                type="checkbox"
                                id={`field-${f.id}`}
                                label={<span>{f.label} <code style={{ fontSize: '0.75rem' }}>{f.path}</code></span>}
                                checked={selectedFields.includes(f.id)}
                                onChange={() => toggleField(f.id)}
                            />
                            {isManualSource && selectedFields.includes(f.id) && renderManualInput(f)}
                            {selectedFields.includes(f.id) && renderFieldSelector(f.id)}
                        </div>
                    ))}
                </Col>
            </Row>

            {/* Property amenities (separate collection, not a field on the property) */}
            <Row className="mb-3">
                <Col>
                    <h6>Property Amenities</h6>
                    <Form.Check
                        type="checkbox"
                        id="copy-property-amenities"
                        label="Copy assigned property amenities"
                        checked={copyAmenities}
                        disabled={isManualSource || !sourceProperty}
                        onChange={() => setCopyAmenities((prev) => !prev)}
                    />
                    {(isManualSource || !sourceProperty) && (
                        <div className="ms-4 text-muted" style={{ fontSize: '0.75rem' }}>
                            Select a source property to copy its amenities.
                        </div>
                    )}
                    {copyAmenities && sourceProperty && (
                        <div className="ms-4 mt-2">
                            <CopyAmenitiesOptions
                                idPrefix="bulk-amenity"
                                sourceAmenities={sourceAmenities}
                                selectedAmenityIds={selectedAmenityIds}
                                onToggleAmenity={(amenityId) => toggleSelection(setSelectedAmenityIds, amenityId)}
                                mode={amenityMode}
                                onModeChange={setAmenityMode}
                            />
                        </div>
                    )}
                </Col>
            </Row>

            {/* Property filter */}
            <Row className="mb-2">
                <Col md={6}>
                    <Form.Control
                        type="text"
                        placeholder="Filter properties by title or ID…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        size="sm"
                    />
                </Col>
                <Col md={6} className="d-flex align-items-center gap-2">
                    <Form.Check
                        type="checkbox"
                        id="public-only"
                        label="Show public properties only"
                        checked={publicPropertiesOnly}
                        onChange={() => setPublicPropertiesOnly((prev) => !prev)}
                    />
                </Col>
            </Row>

            <Row className="mb-4">
                {/* Source selector */}
                <Col md={4}>
                    <h6>Source Property</h6>
                    <Form.Select
                        value={sourceId}
                        onChange={(e) => {
                            setSourceId(e.target.value);
                            setTargetIds((prev) => prev.filter((id) => id !== e.target.value));
                            setSelectedLinkedServices([]);
                            setSelectedAboutSections([]);
                            setSelectedAmenityIds([]);
                            setResultMessage('');
                        }}
                        size="sm"
                    >
                        <option value="">— Select source —</option>
                        <option value={MANUAL_SOURCE_ID}>✎ Manual Entry</option>
                        {filteredList.map((p) => (
                            <option key={p._id} value={p._id}>
                                {p.title}
                            </option>
                        ))}
                    </Form.Select>
                    {sourceProperty && (
                        <p className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                            ID: {sourceProperty._id}
                        </p>
                    )}
                    {isManualSource && (
                        <p className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                            Enter values manually for each selected field below.
                        </p>
                    )}
                </Col>

                {/* Target checkboxes */}
                <Col md={8}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 className="mb-0">Target Properties ({targetIds.length} selected)</h6>
                        <Button size="sm" variant="outline-secondary" onClick={selectAllTargets}>
                            Select All
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setTargetIds([])}>
                            Clear
                        </Button>
                    </div>
                    <div
                        style={{
                            maxHeight: 280,
                            overflowY: 'auto',
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            padding: '0.5rem',
                        }}
                    >
                        {filteredList
                            .filter((p) => p._id !== sourceId)
                            .map((p) => (
                                <Form.Check
                                    key={p._id}
                                    type="checkbox"
                                    id={`target-${p._id}`}
                                    label={`${p.title}`}
                                    checked={targetIds.includes(p._id)}
                                    onChange={() => toggleTarget(p._id)}
                                />
                            ))}
                    </div>
                </Col>
            </Row>

            {/* Preview */}
            {canApply && (
                <Row className="mb-3">
                    <Col>
                        <h6>Preview</h6>
                        <p style={{ fontSize: '0.875rem' }}>
                            Copying{' '}
                            <strong>
                                {[
                                    ...COPYABLE_FIELDS.filter((f) => selectedFields.includes(f.id)).map((f) => f.label),
                                    ...(amenitiesEnabled ? ['Property Amenities'] : []),
                                ].join(', ')}
                            </strong>{' '}
                            from <strong>{isManualSource ? 'manually entered values' : sourceProperty!.title}</strong> to{' '}
                            <strong>{targetIds.length}</strong> propert{targetIds.length === 1 ? 'y' : 'ies'}.
                        </p>
                        <Table striped bordered size="sm" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Title</th>
                                </tr>
                            </thead>
                            <tbody>
                                {targetIds.map((id) => (
                                    <tr key={id}>
                                        <td>{id}</td>
                                        <td>{(myProperties[id] as PropertyType)?.title}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col>
                    <Button variant="primary" onClick={() => handleApply(false)} disabled={!canApply}>
                        Apply Copy
                    </Button>
                    <Button variant="secondary" onClick={() => handleApply(true)} disabled={!canApply} className="ms-2">
                        Preview
                    </Button>
                    {resultMessage && (
                        <p className="mt-2 text-success" style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                            {resultMessage}
                        </p>
                    )}
                </Col>
            </Row>
        </Container>
    );
}
